import { useMemo } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import {
  ActionType,
  ActorTypeSystem,
  ActorTypeWeave,
  AuditActor,
  AuditEvent,
} from '@weave/schema-gen-ts/dist/apis/api/phone/audits/v1/api.pb';
import { i18next, useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useSettingsNavigate } from '@frontend/settings-routing';
import {
  Avatar,
  Button,
  Chip,
  InfoIcon,
  PopoverDialog,
  Table,
  TableColumnConfig,
  Text,
  TextLink,
  Truncated,
  usePopoverDialog,
  useTooltip,
} from '@frontend/design-system';
import {
  ACTION_CHIP_CONFIG,
  OBJECT_SETTINGS_PATH,
  SETTING_TYPE_LABELS,
  UNKNOWN_LABEL,
  WEAVE_USER_LABEL,
} from './phone-system-history-config';
import { AuditEventFilters, PhoneSystemHistoryFilters } from './phone-system-history-filters';
import { AdditionalContext, formatEventTime, getEventSummaryString } from './utils/audit-formatters';
import { PhoneSystemHistoryTrackingIds } from './utils/tracking-ids';

type PhoneSystemHistoryTableProps = {
  events: AuditEvent[];
  isLoading: boolean;
  canEditUsers: boolean;
  // Filters
  filters: AuditEventFilters;
  onFiltersChange: (filters: AuditEventFilters) => void;
  onDetailsClick: (event: AuditEvent) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  isCompact?: boolean; // True when the details tray is open and squishing the table.
  selectedEventId?: string;
  // Pagination
  pageNumber: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onPageChange: (direction: 'next' | 'prev') => void;
  onPageSizeChange: (size: number) => void;
};

const selectedRowStyles = css`
  background-color: var(--fabric-table-row-selected);
`;

export function PhoneSystemHistoryTable({
  events,
  isLoading,
  canEditUsers,
  filters,
  onFiltersChange,
  searchValue,
  setSearchValue,
  onDetailsClick,
  isCompact,
  selectedEventId,
  pageNumber,
  pageSize,
  hasNextPage,
  hasPreviousPage,
  onPageChange,
  onPageSizeChange,
}: PhoneSystemHistoryTableProps) {
  const { t } = useTranslation('phone');
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');

  const additionalContext = useMemo(() => ({ settingsTenantLocation }), [settingsTenantLocation]);
  const colConfig = useMemo(
    () => getColumnConfig(t, canEditUsers, setSearchValue, onDetailsClick, additionalContext),
    [t, canEditUsers, setSearchValue, onDetailsClick, additionalContext]
  );

  return (
    <div css={tableContainerStyle}>
      <Table
        tableInstanceId='phone-system-history-table'
        colConfig={colConfig}
        data={events}
        isPaginated
        hasResizeColumns
        globalTrackingId={PhoneSystemHistoryTrackingIds.table.globalTrackingId}
        manualPaginationConfig={{
          page: pageNumber,
          hasNext: hasNextPage,
          hasPrevious: hasPreviousPage,
          showGoToPageField: false,
          showNumRowSelector: true,
          defaultRowsPerPage: pageSize,
          rowsPerPageOptions: [25, 50, 100],
          onNumRowsChange: onPageSizeChange,
          handleChange: onPageChange,
        }}
        isLoading={isLoading}
        styleConfig={{
          rows: (rowData: AuditEvent): SerializedStyles | undefined =>
            selectedEventId && rowData.id === selectedEventId ? selectedRowStyles : undefined,
        }}
        emptyStateConfig={{
          header: t('No Matching History'),
          description: t('No history matches your filters or search.'),
        }}
        hasResponsiveColWidths
        customToolbarRender={() => (
          <PhoneSystemHistoryFilters
            filters={filters}
            onFiltersChange={onFiltersChange}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            isCompact={isCompact}
          />
        )}
        wrapperStyle={tableToolbarStyle}
      />
    </div>
  );
}

function getColumnConfig(
  t: (key: string) => string,
  canEditUsers: boolean,
  setSearchValue: (value: string) => void,
  onDetailsClick: (event: AuditEvent) => void,
  additionalContext: AdditionalContext
): TableColumnConfig<AuditEvent>[] {
  return [
    {
      Header: t('Current Name'),
      id: 'currentName',
      accessor: (event: AuditEvent) => event,
      cellRenderer: (event: AuditEvent) => <CurrentNameCell event={event} />,
      width: 200,
      disableSortBy: true,
    },
    {
      Header: t('Setting Type'),
      id: 'settingType',
      accessor: (event: AuditEvent) => SETTING_TYPE_LABELS[event.objectType ?? '']?.label ?? UNKNOWN_LABEL,
      width: 160,
      disableSortBy: true,
    },
    {
      Header: t('Action'),
      id: 'action',
      accessor: (event: AuditEvent) => event.action,
      cellRenderer: (action: ActionType) => {
        const config = ACTION_CHIP_CONFIG[action ?? ''] ?? { label: UNKNOWN_LABEL, variant: 'warn' as const };
        return <Chip variant={config.variant}>{config.label}</Chip>;
      },
      width: 100,
      disableSortBy: true,
    },
    {
      Header: t('Change Details'),
      id: 'details',
      accessor: (event: AuditEvent) => event,
      cellRenderer: (event: AuditEvent) => {
        const detail = getDetailsText(event, additionalContext);
        return (
          <Truncated
            as={TextLink}
            textAlign='left'
            onClick={() => onDetailsClick(event)}
            trackingId={PhoneSystemHistoryTrackingIds.table.detailsTextLink}
          >
            {detail}
          </Truncated>
        );
      },
      width: 280,
      disableSortBy: true,
    },
    {
      Header: t('Date and Time'),
      id: 'date',
      accessor: (event: AuditEvent) => event.createTime,
      cellRenderer: (createTime: string) => {
        const formatted = formatEventTime(createTime);
        return <Text textAlign='left'>{formatted}</Text>;
      },
      width: 260,
      disableSortBy: true,
    },
    {
      Header: t('User'),
      id: 'updatedBy',
      accessor: (event: AuditEvent) => event.actor,
      cellRenderer: (actor: AuditActor) => <UpdatedByCell actor={actor} canEditUsers={canEditUsers} />,
      width: 230,
      disableSortBy: true,
    },
    {
      Header: <ObjectIdHeader />,
      id: 'objectId',
      accessor: (event: AuditEvent) => event.objectId ?? '',
      cellRenderer: (objectId: string) => (
        <Truncated as={Text} textAlign='left'>
          {objectId || '--'}
        </Truncated>
      ),
      width: 240,
      disableSortBy: true,
    },
    {
      Header: '',
      id: 'actions',
      accessor: (event: AuditEvent) => event,
      sticky: 'right' as const,
      cellRenderer: (event: AuditEvent) => <RowSearchButton event={event} setSearchValue={setSearchValue} />,
      width: 50,
      disableSortBy: true,
    },
  ];
}

function RowSearchButton({ event, setSearchValue }: { event: AuditEvent; setSearchValue: (value: string) => void }) {
  const { t } = useTranslation('phone');
  const objectId = event.objectId ?? '';
  const objectName = event.objectName || UNKNOWN_LABEL;

  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'top' });

  return (
    <>
      <div {...triggerProps} css={rowSearchButtonWrapperStyle}>
        <Button
          iconName='search'
          variant='secondary'
          disabled={!objectId}
          onClick={() => setSearchValue(objectId)}
          trackingId={PhoneSystemHistoryTrackingIds.table.rowSearchButton}
        />
      </div>

      <Tooltip {...tooltipProps}>{t('Search for all changes related to {{objectName}}', { objectName })}</Tooltip>
    </>
  );
}

function CurrentNameCell({ event }: { event: AuditEvent }) {
  const { navigate } = useSettingsNavigate();
  const settingsPath = OBJECT_SETTINGS_PATH[event.objectType ?? ''];

  if (!settingsPath) {
    return <Text textAlign='left'>{event.objectName}</Text>;
  }

  return (
    <TextLink
      onClick={() => navigate({ to: settingsPath })}
      textAlign='left'
      trackingId={PhoneSystemHistoryTrackingIds.table.nameTextLink}
    >
      {event.objectName}
    </TextLink>
  );
}

function ObjectIdHeader() {
  const { t } = useTranslation('phone');
  const { getDialogProps, getTriggerProps } = usePopoverDialog({ placement: 'bottom' });

  return (
    <div css={objectIdHeaderStyle}>
      <span>{t('Object ID')}</span>

      <Button
        {...getTriggerProps()}
        variant='tertiary'
        size='small'
        css={objectIdInfoButtonStyle}
        trackingId={PhoneSystemHistoryTrackingIds.table.objectIdColumnInfoIcon}
      >
        <InfoIcon size={14} />
      </Button>

      <PopoverDialog {...getDialogProps()} css={objectIdPopoverStyle}>
        <Text weight='bold'>{t('Object ID')}</Text>
        <Text size='medium'>
          {t(
            'This is the unique identifier of any Phone System object in your Phone System. For example, this could be a specific Call Group or Call Route.'
          )}
        </Text>
        <Text size='medium'>
          {t('This identifier stays consistent even if a Phone System object is deleted or renamed.')}
        </Text>
      </PopoverDialog>
    </div>
  );
}

function UpdatedByCell({ actor, canEditUsers }: { actor: AuditActor; canEditUsers: boolean }) {
  const { navigate } = useSettingsNavigate();
  const { t } = useTranslation('phone');
  const isSystem = actor?.type === ActorTypeSystem;
  const isWeaveEmployee = actor?.type === ActorTypeWeave;

  const { Tooltip, tooltipProps, triggerProps } = useTooltip();

  if (isSystem) {
    return (
      <div css={updatedByCellStyle}>
        <Avatar iconName='weave-logo' size='xs' disableHoverLabel />
        <Text size='medium'>{actor?.displayName || t('Weave System')}</Text>
      </div>
    );
  }

  if (isWeaveEmployee) {
    return (
      <div css={updatedByCellStyle}>
        <Avatar iconName='weave-logo' size='xs' disableHoverLabel />
        <Text size='medium'>
          {actor?.displayName} <span css={weaveBadgeStyle}>{`(${WEAVE_USER_LABEL})`}</span>
        </Text>
      </div>
    );
  }

  const canLinkToUser = canEditUsers && !!actor?.id;
  const hasEmail = !!actor?.email;

  return (
    <div css={updatedByCellStyle}>
      <Avatar name={actor?.displayName ?? ''} size='xs' disableHoverLabel />
      {canLinkToUser ? (
        <TextLink
          css={updatedByLinkStyle}
          onClick={() => navigate({ to: '/organization/users/:id', params: { id: actor?.id ?? '' } })}
          trackingId={PhoneSystemHistoryTrackingIds.table.updatedByTextLink}
          {...(hasEmail ? triggerProps : {})}
        >
          {actor?.displayName}
        </TextLink>
      ) : (
        <Text size='medium' {...(hasEmail ? triggerProps : {})}>
          {actor?.displayName}
        </Text>
      )}

      {hasEmail && (
        <Tooltip {...tooltipProps}>
          <Text size='medium' color='white'>
            {actor.email}
          </Text>
        </Tooltip>
      )}
    </div>
  );
}

// Returns a string that summarizes the changes in an audit event. If there are no changes, it returns '--'. If there
// is one change, it returns the summary of that change. If there are multiple changes, it returns a string indicating
// the number of changes.
function getDetailsText(event: AuditEvent, additionalContext?: AdditionalContext): string {
  const { changes } = event;
  if (!changes || changes.length === 0) return '--';

  if (changes.length === 1) {
    const summary = getEventSummaryString(event, additionalContext);
    return summary || '--';
  }

  return i18next.t('{{count}} changes', { count: changes.length, ns: 'phone' });
}

// --- Styles ---

const tableContainerStyle = css`
  background-color: #ffffff;
  border-radius: var(--fabric-radius-md);
  padding: var(--fabric-space-padding-lg);
  box-shadow: var(--fabric-card-shadow);
`;

const tableToolbarStyle = css`
  .table-toolbar {
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-sm);
  }
`;

const objectIdHeaderStyle = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-xxs);
`;

const objectIdInfoButtonStyle = css`
  padding: var(--fabric-space-padding-xxs);
  min-width: unset;
  color: var(--colors-neutral-50);
`;

const objectIdPopoverStyle = css`
  padding: var(--fabric-space-padding-md);
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xs);
`;

const updatedByCellStyle = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-xs);
`;

const weaveBadgeStyle = css`
  color: var(--colors-neutral-50);
`;

const updatedByLinkStyle = css`
  text-align: left;
`;

const rowSearchButtonWrapperStyle = css`
  display: inline-flex;
`;
